import { FINAL_FOR_CLIENT, type Payment, type PaymentMethod, type PaymentsConfig } from '@trendator/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { CheckIcon, ChevronDim, CrossIcon } from '../components/icons';
import { rub } from '../format';
import { useBackButton } from '../hooks';
import { haptic, hapticResult, openPayUrl } from '../telegram';
import { Loading } from './Status';
import type { TopUpPreset } from './TopUp';

const POLL_MS = 3000;

/** Ждём оплату → PayDone / PayFail (docs/design/PayDone, PayFail). */
export function PaymentStatus({
  paymentId,
  onBalance,
  onHome,
  onRetry,
  onPaid,
}: {
  paymentId: string;
  /** Новый баланс — обновить шапку. */
  onBalance: (balance: number) => void;
  onHome: () => void;
  onRetry: (preset: TopUpPreset) => void;
  /** Оплата прошла (один раз) — например, запустить отложенную генерацию. */
  onPaid?: (payment: Payment) => void;
}) {
  useBackButton(onHome);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [missing, setMissing] = useState(false);
  const notified = useRef(false);
  const loaded = useRef(false);
  const onBalanceRef = useRef(onBalance);
  onBalanceRef.current = onBalance;

  const load = useCallback(async () => {
    try {
      const p = await api.payment(paymentId);
      loaded.current = true;
      setPayment(p);
      onBalanceRef.current(p.balance);
      return p;
    } catch {
      // Ни разу не загрузили — платежа нет (чужой/битая ссылка). Иначе — временный сбой, опрос продолжится.
      if (!loaded.current) setMissing(true);
      return null;
    }
  }, [paymentId]);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      const p = await load();
      if (stopped) return;
      if (!loaded.current && !p) return;
      if (!p || !FINAL_FOR_CLIENT.includes(p.status)) timer = setTimeout(tick, POLL_MS);
    };
    void tick();
    // Вернулся из браузера/платёжки — проверить сразу.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => {
    api
      .paymentsConfig()
      .then((c: PaymentsConfig) => setMethods(c.methods))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!payment || notified.current || !FINAL_FOR_CLIENT.includes(payment.status)) return;
    notified.current = true;
    hapticResult(payment.status === 'paid' ? 'success' : 'error');
    if (payment.status === 'paid') onPaid?.(payment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  if (missing && !payment) {
    return (
      <main className="screen screen--hero">
        <div className="center" style={{ minHeight: 'calc(80 * var(--dvh))' }}>
          <h1>Платёж не найден</h1>
          <p>Если деньги списались — баланс пополнится сам в течение пары минут.</p>
          <button type="button" className="btn-accent" style={{ maxWidth: 280 }} onClick={onHome}>
            На главную
          </button>
        </div>
      </main>
    );
  }
  if (!payment) return <Loading />;

  if (payment.status === 'paid') return <PayDone payment={payment} onHome={onHome} />;
  if (FINAL_FOR_CLIENT.includes(payment.status)) {
    return <PayFail payment={payment} methods={methods} onRetry={onRetry} />;
  }
  return <Waiting payment={payment} onCancel={onHome} />;
}

function TestChip({ show }: { show: boolean }) {
  return show ? <span className="test-chip">Тестовый режим</span> : null;
}

function Waiting({ payment, onCancel }: { payment: Payment; onCancel: () => void }) {
  return (
    <main className="screen screen--hero result">
      <div className="result__head">
        <div className="spinner spinner--big" role="status" aria-label="Ждём оплату" />
        <h1 className="result__title result__title--md">Ждём оплату</h1>
        <p className="result__text">
          {rub(payment.amount)} → {payment.credits.toLocaleString('ru-RU')} кредитов.
          <br />
          Оплати на странице платёжки — баланс обновится сам.
        </p>
        <TestChip show={payment.test} />
      </div>
      <div className="result__footer">
        {payment.payUrl && (
          <button
            type="button"
            className="btn-accent"
            onClick={() => {
              haptic();
              openPayUrl(payment.payUrl!);
            }}
          >
            Перейти к оплате
          </button>
        )}
        <button type="button" className="btn-glass" onClick={onCancel}>
          Отменить
        </button>
      </div>
    </main>
  );
}

function PayDone({ payment, onHome }: { payment: Payment; onHome: () => void }) {
  return (
    <main className="screen result result--done">
      <div className="result__head">
        <div className="result__badge result__badge--ok">
          <CheckIcon />
        </div>
        <h1 className="result__title">+{rub(payment.credits)}</h1>
        <p className="result__text">Баланс пополнен · теперь {rub(payment.balance)}</p>
        {payment.bonus > 0 && <p className="result__note">в том числе бонус +{rub(payment.bonus)}</p>}
        <TestChip show={payment.test} />
      </div>
      <p className="result__small">Чек отправили в чат с ботом. Кредиты не сгорают.</p>
      <div className="result__footer">
        <button type="button" className="btn-glass" onClick={onHome}>
          К трендам
        </button>
      </div>
    </main>
  );
}

const ALT: Record<PaymentMethod, { label: string; tag: string; bg: string }> = {
  card_sbp: { label: 'Через СБП — проходит чаще', tag: 'СБП', bg: 'linear-gradient(135deg, #6FD3FF, #3B6BD9)' },
  usdt: { label: 'Криптой', tag: 'USDT', bg: 'linear-gradient(135deg, #7CF2C8, #26A17B)' },
};

function PayFail({
  payment,
  methods,
  onRetry,
}: {
  payment: Payment;
  methods: PaymentMethod[];
  onRetry: (preset: TopUpPreset) => void;
}) {
  const text =
    payment.status === 'expired'
      ? 'Время на оплату вышло. Деньги не списались.'
      : payment.status === 'canceled'
        ? 'Деньги не списались. Банк отклонил платёж — так бывает.'
        : 'Платёж отменили, кредиты по нему списаны. Если это ошибка — напиши в поддержку.';
  const { packageId } = payment;
  return (
    <main className="screen result result--fail">
      <div className="result__head">
        <div className="result__badge result__badge--fail">
          <CrossIcon />
        </div>
        <h1 className="result__title result__title--md">Оплата не прошла</h1>
        <p className="result__text">{text}</p>
      </div>

      {methods.length > 0 && (
        <div className="glass-list" style={{ margin: '32px 16px 0' }}>
          <span className="glass-list__caption">Попробуй по-другому</span>
          {methods.map((m, i) => (
            <div key={m}>
              {i > 0 && <div className="glass-list__sep" style={{ marginLeft: 62 }} />}
              <button
                type="button"
                className="glass-list__row glass-list__row--btn"
                onClick={() => onRetry({ packageId, method: m })}
              >
                <span className="glass-list__icon glass-list__icon--tag" style={{ background: ALT[m].bg }}>
                  {ALT[m].tag}
                </span>
                <span className="glass-list__label">{ALT[m].label}</span>
                <ChevronDim />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="result__footer">
        <button type="button" className="btn-accent" onClick={() => onRetry({ packageId, method: payment.method })}>
          Попробовать ещё раз
        </button>
      </div>
    </main>
  );
}
